import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { typography } from '../../../styles/typography';
import { stockInApi } from '../../Onboarding/Stock_in/stockInApi';
import Card from '../../ui/card';

/**
 * Fetches all stock items from the API with pagination support
 * @param {Object} params - Query parameters for filtering
 * @returns {Promise<Array>} Array of all stock items
 */
const fetchAllStockItems = async (params = {}) => {
  const allItems = [];
  let lastKey = null;
  let hasMore = true;

  while (hasMore) {
    const requestParams = {
      limit: 100, // Maximum allowed per request
      ...params,
    };

    if (lastKey) {
      requestParams.last_key = lastKey;
    }

    const result = await stockInApi.listStockItems(requestParams);

    if (result.success && result.items) {
      allItems.push(...result.items);
      lastKey = result.last_key;
      hasMore = !!lastKey; // Continue if there's a pagination key
    } else {
      hasMore = false;
      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch stock items');
      }
    }
  }

  return allItems;
};

const AllStockItems = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [stockData, setStockData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Debounce search term to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch stock items from API
  useEffect(() => {
    const loadStockItems = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Build query parameters
        const queryParams = {};
        
        if (debouncedSearchTerm.trim()) {
          queryParams.name_contains = debouncedSearchTerm.trim();
        }

        // Fetch all stock items (with pagination handled internally)
        const items = await fetchAllStockItems(queryParams);

        // Transform API response to match component structure
        // The API returns items directly, so we'll structure them for the UI
        const transformedData = {
          top_used_stock: items,
          least_used_stock: [],
        };

        setStockData(transformedData);
      } catch (err) {
        console.error('Error loading stock items:', err);
        setError(err.message || 'Failed to load stock items. Please try again.');
        setStockData({ top_used_stock: [], least_used_stock: [] });
      } finally {
        setIsLoading(false);
      }
    };

    // Always fetch from API to allow search functionality
    loadStockItems();
  }, [debouncedSearchTerm]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200 border-t-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading stock items...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && !isLoading) {
    return (
      <Card.Gradient className="p-12 text-center">
        <div className="max-w-md mx-auto">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className={typography.h4} style={{ color: '#000000', marginBottom: '0.5rem' }}>
            Error Loading Stock Items
          </h3>
          <p className={typography.p} style={{ color: '#666666', marginBottom: '1rem' }}>
            {error}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors"
          >
            Retry
          </button>
        </div>
      </Card.Gradient>
    );
  }

  // Combine all items for display
  const allItems = [
    ...(stockData?.top_used_stock || []),
    ...(stockData?.least_used_stock || [])
  ].sort((a, b) => (a.item_name || '').localeCompare(b.item_name || ''));

  return (
    <div className="w-full h-full">
      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className={`${typography.h2} text-black mb-1`}>All Stock Items</h1>
          <p className={`${typography.p} text-gray-600 text-sm`}>
            Complete inventory list with quantities and units
            {allItems.length > 0 && ` (${allItems.length} items)`}
          </p>
        </div>
        {/* <button
          onClick={() => navigate('/dashboard-stock-dashboard')}
          className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors"
        >
          Back to Dashboard
        </button> */}
      </div>

      {/* Filters Section */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search Input */}
          <div className="max-w-[320px] w-full">
            <input
              type="text"
              placeholder="Search by item name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Stock Items Grid */}
      {allItems.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {allItems.map((item, index) => {
            const isLowStock = item.reorder_threshold > 0 && 
                               item.current_quantity <= item.reorder_threshold;
            
            return (
              <Card.Gradient
                key={item.stock_item_id || index}
                className={`p-4 transition-colors ${
                  isLowStock 
                    ? 'border-2 border-red-300 hover:border-red-500' 
                    : 'hover:border-green-500'
                }`}
              >
                <div className="flex flex-col">
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 flex-1">
                      {item.item_name || 'Unknown Item'}
                    </span>
                    {isLowStock && (
                      <span className="ml-2 px-2 py-1 text-xs font-semibold text-red-700 bg-red-200 rounded">
                        Low Stock
                      </span>
                    )}
                  </div>
                  <div className="space-y-1">
                    <span className="text-lg font-bold text-gray-900">
                      {item.current_quantity?.toLocaleString('en-IN', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }) || '0'}{' '}
                      {item.unit_of_measure || 'units'}
                    </span>
                    {item.reorder_threshold > 0 && (
                      <p className="text-xs text-gray-500">
                        Reorder at: {item.reorder_threshold} {item.unit_of_measure}
                      </p>
                    )}
                    {item.vendor_name && (
                      <p className="text-xs text-gray-500">
                        Vendor: {item.vendor_name}
                      </p>
                    )}
                  </div>
                </div>
              </Card.Gradient>
            );
          })}
        </div>
      ) : (
        <Card.Gradient className="p-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <h3 className={typography.h4} style={{ color: '#000000', marginBottom: '0.5rem' }}>
              No stock items available
            </h3>
            <p className={typography.p} style={{ color: '#666666' }}>
              {searchTerm 
                ? 'No items match your search criteria. Try adjusting your search term.'
                : 'Please try again later'}
            </p>
          </div>
        </Card.Gradient>
      )}
    </div>
  );
};

export default AllStockItems;

